import * as React from 'react'

import { Trash2Icon } from 'lucide-react'
import { makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  IChecklistItem,
  IPermitTypeChecklist,
  IPermitTypeField,
} from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import QuestionnaireType from '~/client/src/shared/enums/QuestionnaireType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { getBullet } from '~/client/src/shared/utils/PermitInstructionsHelper'

import BaseInstructionsConfigurator from '../BaseInstructionsConfigurator/BaseInstructionsConfigurator'
import InstructionTextHolder from '../BaseInstructionsConfigurator/InstructionTextHolder'
import { FieldState } from '../FieldConfigurator/FieldConfigurator.store'
import FieldStateSelector from '../FieldStateSelector'
import TypeSelector from '../TypeSelector'
import ChecklistStore from './Checklist.store'

interface IProps {
  field: IPermitTypeField
  onChange(checklist: IPermitTypeChecklist): void
  onFieldChange(field: IPermitTypeField): void
  className?: string
  isSingleMode?: boolean
}

@observer
export default class Checklist extends React.Component<IProps> {
  @observable private isSingleMode: boolean = false
  private readonly store: ChecklistStore = null

  public constructor(props: IProps) {
    super(props)

    this.isSingleMode = props.isSingleMode
    this.store = new ChecklistStore(
      props.field,
      props.onChange,
      props.onFieldChange,
    )
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    const { field, onFieldChange } = this.props
    if (field !== prevProps.field) {
      this.store.setField(field)
    }
    if (onFieldChange !== prevProps.onFieldChange) {
      this.store.setOnFieldChangeHandler(onFieldChange)
    }
    if (this.isSingleMode !== this.props.isSingleMode) {
      this.isSingleMode = this.props.isSingleMode
    }
  }

  public render() {
    const { field, onChange, className } = this.props

    return (
      <BaseInstructionsConfigurator<IChecklistItem>
        className={className}
        instructions={field.checklist}
        onChange={onChange}
        store={this.store}
        instructionItemRenderer={this.renderItem}
        singleModeElement={this.questionDescriptionElement}
        fieldStateElement={this.fieldStateElement}
        fieldType={field.type}
      />
    )
  }

  private renderItem = (
    { id, text, questionnaireType }: IChecklistItem,
    index: number,
  ) => {
    const {
      handleMouseEnter,
      handleMouseLeave,
      instructions: { listType },
      hoveredInstructionIndex,
      tryToRemoveInstruction,
      questionnaireTypeAsOptions,
    } = this.store

    return (
      <div
        key={id}
        className={classList({
          'row y-start relative instruction-item tw:pr-8': true,
          mb15: !this.isSingleMode,
        })}
        onMouseEnter={handleMouseEnter.bind(null, index)}
        onMouseLeave={handleMouseLeave}
      >
        {!this.isSingleMode && (
          <span className="text large bold blue-highlight no-grow mr12">
            {getBullet(listType, index + 1)}
          </span>
        )}
        <InstructionTextHolder<IChecklistItem>
          instructionText={text}
          instructionIndex={index}
          store={this.store}
        />
        <TypeSelector
          selectedValue={questionnaireType}
          options={questionnaireTypeAsOptions}
          onChange={this.handleQuestionnaireTypeChange.bind(this, id)}
        />
        {!this.isSingleMode && hoveredInstructionIndex === index && (
          <Button
            variant="destructive-ghost"
            size="icon-sm"
            className="del-icon tw:absolute tw:right-0 tw:top-0"
            aria-label={Localization.translator.delete}
            onClick={tryToRemoveInstruction.bind(null, index)}
          >
            <Trash2Icon />
          </Button>
        )}
      </div>
    )
  }

  private get fieldStateElement(): JSX.Element {
    return (
      <div className="row x-end">
        <FieldStateSelector
          className="brada4 bg-palette-brand-lightest"
          fieldState={this.store.fieldState}
          onChange={this.handleFieldStateChange}
        />
      </div>
    )
  }

  private get questionDescriptionElement(): JSX.Element {
    if (!this.props.isSingleMode) {
      return null
    }

    return (
      <span className="text large bold mr15 no-white-space-wrap">
        {Localization.translator.isQuestionMandatory}
      </span>
    )
  }

  private handleQuestionnaireTypeChange = (
    checklistItemId: string,
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.store.changeQuestionnaireTypeForChecklistItem(
      checklistItemId,
      event.target.value as QuestionnaireType,
    )
  }

  private handleFieldStateChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.store.changeFieldState(event.target.value as FieldState)
  }
}
